/**
 * Notes from Jayashri signup forms (snippets/kavacha-notes-form.liquid).
 *
 * Submits Shopify's native customer form in place: the thank-you or error line appears where the
 * field was, announced by its polite status region, with no page jump. Shopify answers a good post
 * with a redirect to a URL carrying `customer_posted=true`; a post with errors re-renders the page
 * with the form's errors.
 *
 * Shopify protects customer forms with an invisible hCaptcha (storefront-forms-hcaptcha): its own
 * submit listener fetches a token, adds `h-captcha-response` and posts natively. A post without the
 * token gets 403. So this script listens first (document, capture phase), asks the same widget for
 * the token and posts it with fetch. Whenever that is not possible (no widget yet, a challenge, a
 * refused post), it hands the submit back to Shopify's own flow, which always works, with a page load.
 *
 * A successful signup is remembered in localStorage (`kv-notes`), so the "What would you keep
 * inside?" sheet never asks again. Storage can be missing or blocked; every access is guarded.
 */

export const NOTES_KEY = 'kv-notes';

/**
 * @param {'subscribed' | 'dismissed' | 'shown'} state
 */
export function rememberNotes(state) {
  try {
    const current = JSON.parse(localStorage.getItem(NOTES_KEY) || 'null');
    // A signup outranks everything; never downgrade it.
    if (current?.state === 'subscribed' && state !== 'subscribed') return;
    localStorage.setItem(NOTES_KEY, JSON.stringify({ state, at: Date.now() }));
  } catch {
    // Storage blocked: nothing to remember, and the sheet will not show without it.
  }
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class KavachaNotesForm extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;
  #busy = false;
  #handOff = false;

  connectedCallback() {
    this.#form = this.querySelector('form');
    if (!this.#form || this.#form.dataset.kvReady) return;
    this.#form.dataset.kvReady = 'true';
    this.#form.noValidate = true;

    const status = this.#status;
    if (status?.dataset.state === 'success') {
      rememberNotes('subscribed');
    }
  }

  get #input() {
    return /** @type {HTMLInputElement | null} */ (this.#form?.querySelector('input[type="email"]') ?? null);
  }

  get #button() {
    return /** @type {HTMLButtonElement | null} */ (this.#form?.querySelector('button[type="submit"]') ?? null);
  }

  get #status() {
    return /** @type {HTMLElement | null} */ (this.#form?.querySelector('.kv-notes-form__status') ?? null);
  }

  /**
   * @param {'success' | 'error' | ''} state
   * @param {string} message
   */
  #show(state, message) {
    const status = this.#status;
    if (!status) return;
    if (state) {
      status.dataset.state = state;
    } else {
      delete status.dataset.state;
    }
    status.textContent = message;
  }

  /** Let Shopify's own captcha listener and native post take this submit. */
  #giveBack() {
    const form = this.#form;
    if (!form) return;
    this.#handOff = true;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      HTMLFormElement.prototype.submit.call(form);
    }
  }

  /**
   * Ask Shopify's invisible hCaptcha widget on this form for a token. Shopify binds the widget
   * lazily, so wait a moment for it; null when there is none.
   */
  async #captchaToken() {
    const form = this.#form;
    const captcha = /** @type {any} */ (window).hcaptcha;
    if (!form) return null;
    let widgetId = null;
    for (let wait = 0; wait < 20 && !widgetId; wait += 1) {
      widgetId = form.querySelector('.h-captcha iframe[data-hcaptcha-widget-id]')?.getAttribute('data-hcaptcha-widget-id');
      if (!widgetId) await new Promise((resolve) => setTimeout(resolve, 150));
    }
    if (!widgetId || typeof captcha?.execute !== 'function') return null;
    const result = await captcha.execute(widgetId, { async: true });
    return result?.response || null;
  }

  /** @param {SubmitEvent} event */
  handleSubmit = async (event) => {
    const form = this.#form;
    const input = this.#input;
    if (!form || !input) return;
    if (this.#handOff) {
      // Our own requestSubmit(): let Shopify's listener run.
      this.#handOff = false;
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.#busy) return;

    const value = input.value.trim();
    if (!value || !EMAIL_PATTERN.test(value)) {
      input.setAttribute('aria-invalid', 'true');
      this.#show('error', this.dataset.invalid || '');
      input.focus();
      return;
    }
    input.value = value;
    input.removeAttribute('aria-invalid');

    this.#busy = true;
    this.#button?.setAttribute('aria-disabled', 'true');
    this.#show('', '');

    try {
      // Throws when the visitor closes an hCaptcha challenge: the catch below shows the error line
      // rather than reopening the challenge.
      const token = await this.#captchaToken();
      if (form.querySelector('.h-captcha') && !token) {
        this.#giveBack();
        return;
      }
      const body = new FormData(form);
      if (token) body.set('h-captcha-response', token);

      const response = await fetch(form.action, { method: 'POST', body, credentials: 'same-origin' });
      const url = new URL(response.url, window.location.href);

      if (response.ok && url.searchParams.get('customer_posted') === 'true') {
        this.#succeed();
        return;
      }

      if (!response.ok || url.pathname.startsWith('/challenge')) {
        // Refused or challenged: Shopify's own page flow can finish it.
        this.#giveBack();
        return;
      }

      const message = await this.#errorFrom(response);
      input.setAttribute('aria-invalid', 'true');
      this.#show('error', message || this.dataset.error || '');
      input.focus();
    } catch {
      this.#show('error', this.dataset.error || '');
    } finally {
      this.#busy = false;
      this.#button?.removeAttribute('aria-disabled');
    }
  };

  #succeed() {
    const template = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template.kv-notes-form__success'));
    const message = template?.content.textContent?.trim() || '';
    const hadFocus = this.contains(document.activeElement);
    // The field and button give way to one line; keep the form's height so nothing below moves.
    // (Not in the fixed sheet: it shifts nothing, and it should shrink to fit.)
    if (this.#form && !this.closest('[data-kv-keep]')) {
      this.#form.style.minHeight = `${this.#form.getBoundingClientRect().height}px`;
    }
    this.#show('success', message);
    rememberNotes('subscribed');
    // The field and button are hidden now; keep keyboard focus on the message that replaced them.
    if (hadFocus) this.#status?.focus({ preventScroll: true });
    this.dispatchEvent(new CustomEvent('kv-notes:subscribed', { bubbles: true }));
  }

  /**
   * Shopify re-renders the page with the form's errors; read this form's message line from it.
   * @param {Response} response
   */
  async #errorFrom(response) {
    try {
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const id = this.#status?.id;
      const line = id ? doc.getElementById(id) : null;
      if (line?.dataset.state === 'error') return line.textContent?.trim() || '';
    } catch {
      // Fall through to the generic line.
    }
    return '';
  }
}

if (!customElements.get('kv-notes-form')) {
  customElements.define('kv-notes-form', KavachaNotesForm);
  // Capture phase on the document runs before Shopify's captcha listener on the form.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const host = form instanceof HTMLFormElement ? form.closest('kv-notes-form') : null;
      if (host instanceof KavachaNotesForm) host.handleSubmit(/** @type {SubmitEvent} */ (event));
    },
    true
  );
}
